import {
  observable,
  action,
  computed,
  reaction,
  runInAction,
  flow
} from 'mobx';
import { getSafetyDataForUser } from '../apis/SafetyScoreApi';
import * as safetyData from '../__mockData__/safety.json';
import _ from 'lodash';
import axios from 'axios';

const CancelToken = axios.CancelToken;
const { isString, isNumber } = _;

export default class SafetyStore {
  clearWhenLoggedOut;

  cancelTokens = {
    getSafetyScore: undefined
  };

  @observable
  safetyData = {
    driverScore: null,
    periodEnding: null,
    historyPage: [],
    targetScore: null
  };

  @observable
  loading = {
    getSafetyScore: false
  };

  @observable
  loaded = {
    safetyData: false
  };

  @observable
  loadingErrors = {
    safetyData: false
  };

  constructor(rootStore) {
    this.rootStore = rootStore;
  }

  async init() {
    this.setupLogoutReaction();
  }

  /* istanbul ignore next */
  setupLogoutReaction() {
    this.clearWhenLoggedOut = reaction(
      () => this.rootStore.authStore.isLoggedIn,
      isLoggedIn => {
        if (!isLoggedIn) {
          this.reset();
        }
      },
      {
        fireImmediately: false
      }
    );
  }

  @action
  reset() {
    this.safetyData = {
      driverScore: null,
      periodEnding: null,
      historyPage: [],
      targetScore: null
    };
    this.loading = { getSafetyScore: false };
    this.loaded = {
      safetyData: false
    };
  }

  @computed
  get historyItemsCount() {
    return this.safetyData.historyPage.length;
  }

  getHistoryDataSlice(startingIndex, endingIndex) {
    return computed(() => {
      return this.safetyData.historyPage.slice(startingIndex, endingIndex);
    }).get();
  }

  @computed
  get historyScoreMinMax() {
    let safetyHistory = [...this.safetyData.historyPage];
    return {
      targetReferenceMinData: Math.min(
        Math.min(
          ...safetyHistory
            .filter(p => this.safetyScoreIsValid(p.driverScore))
            .map(p => p.driverScore)
        ) * 0.75,
        Math.min(
          ...safetyHistory
            .filter(p => this.safetyScoreIsValid(p.targetScore))
            .map(p => p.targetScore)
        ) * 0.75
      ),
      targetReferenceMaxData: Math.max(
        Math.max(
          ...safetyHistory
            .filter(p => this.safetyScoreIsValid(p.driverScore))
            .map(p => p.driverScore)
        ) * 1.25,
        Math.max(
          ...safetyHistory
            .filter(p => this.safetyScoreIsValid(p.targetScore))
            .map(p => p.targetScore)
        ) * 1.25
      )
    };
  }

  // Used to verify if data should be display on
  // chart or the label "Not enough data available"
  @computed
  get noHistoryScoresAvailable() {
    const historyData = this.getHistoryDataSlice(0, this.historyItemsCount);
    return historyData.every(
      data => !this.safetyScoreIsValid(data.driverScore)
    );
  }

  @computed
  get driverScoreIsNull() {
    return this.safetyData.driverScore === null;
  }

  @computed
  get targetScoreIsNull() {
    return this.safetyData.targetScore === null;
  }

  // Used to verify if data should be display on chart or the
  // label "Not enouugh data available"
  doesChartHaveNullScores(startingIndex, endingIndex) {
    return computed(() => {
      const historyData = this.getHistoryDataSlice(startingIndex, endingIndex);

      // If they are all valid, no nulls.
      if (
        historyData.every(item => this.safetyScoreIsValid(item.driverScore))
      ) {
        return false;
      }

      // If they are all invalid, "no nulls."
      // todo: rename or refactor this method at some point be cause if they are all null one would expect this to return true, but it doesn't.
      if (
        historyData.every(item => !this.safetyScoreIsValid(item.driverScore))
      ) {
        return false;
      }

      return true;
    }).get();
  }

  shouldUseMockData() {
    return this.rootStore.configStore.config[
      this.rootStore.configStore.configurationKeys.useMockDataInUi
    ];
  }

  @action
  /* istanbul ignore next */
  async loadMockSafetyScore(delay = 1000) {
    return new Promise(resolve => {
      this.loading.getSafetyScore = true;

      setTimeout(() => {
        runInAction(() => {
          this.setStateFromSafetyScoreData(safetyData.safetyScore);
          this.loading.getSafetyScore = false;
        });

        resolve(true);
      }, delay);
    });
  }

  /**
   * Description: Gets safety score data for a given driver
   *
   * @param force {bool} Force a reload of data, even if the data is already in memory.
   */
  getSafetyScore = flow(function*(force = false) {
    if (!force && this.loaded.safetyData) {
      return true;
    }

    const useMockData = this.shouldUseMockData();
    /* istanbul ignore if  */
    if (useMockData) {
      return yield this.loadMockSafetyScore();
    }

    try {
      this.loading.getSafetyScore = true;

      const username = this.rootStore.authStore.getUserName();
      const graphQlHubServiceUrl = this.rootStore.configStore.config[
        this.rootStore.configStore.configurationKeys.graphQlHubServiceUrl
      ];

      this.cancelTokens.getSafetyScore = CancelToken.source();

      const safetyScore = yield getSafetyDataForUser(
        graphQlHubServiceUrl,
        username,
        this.cancelTokens.getSafetyScore.token
      );

      this.setStateFromSafetyScoreData(safetyScore);

      this.loaded.safetyData = true;
      this.loadingErrors.safetyData = false;
      return true;
    } catch (error) {
      if (!axios.isCancel(error)) {
        this.loadingErrors.safetyData = true;
      }
    } finally {
      this.loading.getSafetyScore = false;
      this.cancelTokens.getSafetyScore = undefined;
    }
    return false;
  });

  cancelGetSafetyScore() {
    if (this.cancelTokens.getSafetyScore) {
      this.cancelTokens.getSafetyScore.cancel();
    }
  }

  @action
  setStateFromSafetyScoreData(data) {
    if (data && data.length > 0) {
      // Define row index for the Safety Score to display to user
      const lastDataElementIndex = data.length - 1;

      this.safetyData.driverScore =
        data[lastDataElementIndex] &&
        this.safetyScoreIsValid(data[lastDataElementIndex].driverScore)
          ? Number(data[lastDataElementIndex].driverScore)
          : null;

      this.safetyData.targetScore =
        data[lastDataElementIndex] &&
        this.safetyScoreIsValid(data[lastDataElementIndex].targetScore)
          ? Number(data[lastDataElementIndex].targetScore)
          : null;

      // If no ending date is available then use default date -
      // last Saturday of today's date define above
      if (
        data[lastDataElementIndex] &&
        data[lastDataElementIndex].periodEnding
      ) {
        this.safetyData.periodEnding = data[lastDataElementIndex].periodEnding;
      }

      this.safetyData.historyPage.replace(data);
    }
  }

  /**
   * A valid score is either a string that can be coerced into a number or a number that is 0 or positive.
   * @param {} score
   */
  safetyScoreIsValid(score) {
    if (isString(score)) {
      score = parseInt(score, 10);
    }

    if (isNumber(score)) {
      return score >= 0;
    }

    return false;
  }
}



// WEBPACK FOOTER //
// ./src/stores/SafetyStore.js